<template>
  <a-modal
    :visible="visible"
    :destroy-on-close="true"
    title="Edit Role"
    ok-text="Save"
    @ok="handleUpdateProjectRole"
    @cancel="closeModal"
  >
    <!-- INFO TAB -->
    <a-tabs default-active-key="0">
      <a-tab-pane key="0">
        <template #tab>
          <span>
            <a-badge v-show="$validator.errors.any('tab-info')" status="error" />
            Info
          </span>
        </template>

        <a-form data-vv-scope="tab-info" layout="horizontal">
          <a-form-item :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }" label="User">
            <strong>{{ roleUserName }}</strong>
          </a-form-item>
          <SkInput
            v-model="newProjectRole.title"
            v-validate="'required|max:255'"
            :error="$validator.errors.first('step-info.title')"
            data-vv-name="title"
            label="Title"
            placeholder="Role title"
          />

          <SkInput
            v-if="acl.isAdmin || acl.isCr"
            v-model="newProjectRole.agreement_link"
            :error="$validator.errors.first('step-info.agreement_link')"
            data-vv-name="agreement_link"
            label="Agreement Link"
            placeholder="Role agreement link"
          />

          <a-form-item
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            :validate-status="$validator.errors.first('tab-info.start_date') ? 'error' : ''"
            :help="$validator.errors.first('tab-info.start_date')"
            label="Start date"
          >
            <a-date-picker
              v-validate="'required'"
              :value="parseDate(newProjectRole.start_date)"
              :disabled-date="disableStartDate"
              class="w-100"
              data-vv-name="start_date"
              data-vv-as="start date"
              @change="(d) => onDateChange(d, 'start_date')"
            />
          </a-form-item>

          <a-form-item
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            :validate-status="$validator.errors.first('tab-info.end_date') ? 'error' : ''"
            :help="$validator.errors.first('tab-info.end_date')"
            label="End date"
          >
            <a-date-picker
              v-validate="'required'"
              :value="parseDate(newProjectRole.end_date)"
              :disabled-date="disableEndDate"
              class="w-100"
              data-vv-name="end_date"
              data-vv-as="end date"
              @change="(d) => onDateChange(d, 'end_date')"
            />
          </a-form-item>

          <SkInput
            v-model="newProjectRole.notice_period"
            v-validate="'required|integer|min_value:0'"
            :error="$validator.errors.first('step-info.notice_period')"
            data-vv-name="notice_period"
            data-vv-as="notice period"
            label="Notice Period"
            placeholder="Notice period in days"
          />

          <SkInput
            v-model="newProjectRole.workload"
            v-validate="'required|integer|min_value:0'"
            :error="$validator.errors.first('step-info.workload')"
            data-vv-name="workload"
            label="Workload"
            placeholder="Workload in percentages"
            addon-after="%"
          />

          <a-form-item v-if="acl.isAdmin" :wrapper-col="{ offset: 8, span: 16 }">
            <a-checkbox v-model="newProjectRole.disable_cv">Hide in CV selection</a-checkbox>
          </a-form-item>
          <a-form-item
            v-if="acl.isAdmin || acl.isProjectManager"
            :wrapper-col="{ offset: 8, span: 16 }"
          >
            <a-checkbox v-model="newProjectRole.reportable">Reportable</a-checkbox>
          </a-form-item>
          <a-form-item :label-col="{ span: 8 }" :wrapper-col="{ span: 16 }" label="Time reporting">
            <div v-if="!(subprojects.length > 0)">
              <a-radio-group
                v-if="acl.isAdmin"
                v-model="newProjectRole.automatic_time_reporting"
                button-style="solid"
                @change="(el) => (newProjectRole.automatic_time_reporting = el.target.value)"
              >
                <a-radio-button :value="false">Manual</a-radio-button>
                <a-radio-button :value="true">Automatic</a-radio-button>
              </a-radio-group>
              <a-tag v-else color="blue">
                {{ newProjectRole.automatic_time_reporting ? "Automatic" : "Manual" }}
              </a-tag>
            </div>
            <div v-else>
              <span>Must be manual for projects with subprojects</span>
            </div>
          </a-form-item>
          <a-form-item
            v-if="acl.isAdmin || acl.isProjectManager"
            :wrapper-col="{ offset: 8, span: 16 }"
          >
            <a-checkbox v-model="newProjectRole.require_subproject_reports"
              >Enforce subproject reporting</a-checkbox
            >
          </a-form-item>
        </a-form>
      </a-tab-pane>

      <!-- FINANCIAL TAB -->
      <a-tab-pane v-if="acl.isAdmin || acl.isFinancial || acl.isProjectManager || acl.isCr" key="1">
        <template #tab>
          <span>
            <a-badge v-show="$validator.errors.any('tab-financial')" status="error" />
            Financial
          </span>
        </template>
        <a-form data-vv-scope="tab-financial" layout="horizontal">
          <SkMoney
            v-model="newProjectRole.hourly_rate_cents"
            v-validate="'required|decimal|min_value:0'"
            :error="$validator.errors.first('step-financial.hourly_rate')"
            :currency="newProjectRole.hourly_rate_currency"
            data-vv-name="hourly_rate"
            data-vv-as="hourly rate"
            label="Hourly rate"
            placeholder="Hourly rate"
            @set-currency="(v) => (newProjectRole.hourly_rate_currency = v)"
          />

          <SkMoney
            v-model="newProjectRole.future_hourly_rate_cents"
            v-validate="'required|decimal|min_value:0'"
            :error="$validator.errors.first('step-financial.future_hourly_rate')"
            :currency="newProjectRole.future_hourly_rate_currency"
            data-vv-name="future_hourly_rate"
            data-vv-as="future hourly rate"
            label="Future Hourly rate"
            placeholder="Future Hourly rate"
            @set-currency="(v) => (newProjectRole.future_hourly_rate_currency = v)"
          />

          <a-form-item
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            label="Future hourly date"
          >
            <a-date-picker
              :value="parseDate(newProjectRole.future_hourly_rate_date)"
              class="w-100"
              @change="(d) => onDateChange(d, 'future_hourly_rate_date')"
            />
          </a-form-item>

          <a-form-item
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            :validate-status="
              $validator.errors.first('tab-financial.cost_center_id') ? 'error' : ''
            "
            :help="$validator.errors.first('tab-financial.cost_center_id')"
            label="Cost center"
          >
            <a-select
              v-model="newProjectRole.cost_center_id"
              v-validate="'required'"
              :default-value="newProjectRole.cost_center_id"
              placeholder="Select cost center"
              data-vv-name="cost_center_id"
              data-vv-as="cost center"
            >
              <a-select-option v-for="cc in costCenters" :key="cc.id">
                {{ cc.name }} ({{ cc.manager.name }})
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-form>
      </a-tab-pane>

      <!-- WORK EXPERIENCES TAB -->
      <a-tab-pane key="2">
        <template #tab>
          <span>
            <a-badge v-show="$validator.errors.any('tab-work-experiences')" status="error" />
            Work Experiences
          </span>
        </template>
        <a-form data-vv-scope="tab-work-experiences" layout="horizontal">
          <a-form-item
            :label-col="{ span: 8 }"
            :wrapper-col="{ span: 16 }"
            label="Work experiences"
          >
            <a-select
              v-model="newProjectRole.work_experience_ids"
              mode="multiple"
              show-search
              placeholder="Select work experiences"
              option-filter-prop="children"
            >
              <a-select-option
                v-for="workExperience in workExperienceOptions"
                :key="workExperience.id"
              >
                {{ workExperience.formatted_name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-form>
      </a-tab-pane>
    </a-tabs>
    <a-modal
      :visible="showFutureHourlyRateWarningVisible"
      cancel-text="Go back"
      ok-text="Save anyway"
      ok-type="danger"
      @ok="continueAfterFutureHourlyRateWarning"
      @cancel="cancelAfterFutureHourlyRateWarning"
    >
      <template #title>
        <span>
          <a-icon type="warning" class="mr-2" />
          Future hourly rate date is in the past
        </span>
      </template>
      <div class="future-rate-warning">
        <p class="warning-intro">
          The future hourly rate date is in the past. Saving this role can affect reporting and
          other downstream calculations.
        </p>
        <p class="warning-intro">Do you want to continue?</p>

        <div v-if="loadingFutureHourlyRateReports" class="warning-loading">
          <a-icon type="loading" class="mr-2" />
          Loading reports...
        </div>

        <div v-else>
          <p v-if="impactedFutureHourlyRateReports.length" class="report-section-title">
            Reports that will change:
          </p>

          <div v-if="impactedFutureHourlyRateReports.length" class="report-list">
            <div
              v-for="report in impactedFutureHourlyRateReports"
              :key="report.id"
              class="report-item"
            >
              <div class="report-title">{{ getFutureHourlyRateReportLabel(report) }}</div>
              <div class="report-rate-row">
                <span class="rate-label">Hourly rate</span>
                <span class="rate-change">{{ getFutureHourlyRateText(report) }}</span>
              </div>
            </div>
          </div>

          <p v-else class="report-empty">No reports in this period will change hourly rate.</p>
        </div>
      </div>
    </a-modal>
    <a-modal
      :visible="showWarningVisible"
      ok-text="Yes, save"
      ok-type="danger"
      @ok="continueAfterWarning"
      @cancel="cancelAfterWarning"
    >
      <template #title>
        <span>
          <a-icon type="warning" class="mr-2" />
          Warning
        </span>
      </template>
      You are about to update a role for which an extension exists. This isn't recommended.
      <br />
      Are you sure you want to follow through with the update?
    </a-modal>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, watch, toRef, getCurrentInstance } from "vue";
import moment from "@/date";
import { hasPendingExtension } from "../../roles/_utils/role-extension";
import { CostCenterInterface } from "@/modules/cost_centers/types";
import { BaseAclInterface } from "@/modules/authentication/types";
import { ProjectInterface, NewSubprojectInterface } from "../../projects/types";
import { RoleInterface } from "@/modules/roles/types";
import { ReportInterface } from "@/modules/reports/types";
import { apiGetReports } from "@/modules/reports/_utils/api";
import { apiGetUser } from "@/modules/users/_utils/api";
import { WorkExperienceInterface } from "@/modules/users/types";
import useMixin from "@/useMixin";
import useFilters from "@/useFilters";

// Props
const props = defineProps({
  visible: { type: Boolean, default: false },
  acl: { type: Object as () => BaseAclInterface, default: undefined },
  role: { type: Object as () => RoleInterface, default: undefined },
  costCenters: { type: Array as () => Array<CostCenterInterface>, default: () => [] },
  project: { type: Object as () => ProjectInterface, default: undefined },
  subprojects: { type: Array as () => Array<NewSubprojectInterface>, default: () => [] },
});

// Emits
const emits = defineEmits([
  "get-cost-centers",
  "update-project-role",
  "close",
  "get-subprojects-page",
]);

// Mixins
const { setObject, toMoney, toMoneyCents } = useMixin();

// Filters
const { parseDate } = useFilters();

// Instances
const instance = getCurrentInstance();
const $validator = instance?.proxy.$validator;

// Data properties
const newProjectRole = ref<any>({
  hourly_rate_cents: null,
  hourly_rate_currency: null,
  start_date: null,
  end_date: null,
  title: null,
  user_id: null,
  future_hourly_rate_cents: null,
  future_hourly_rate_currency: null,
  future_hourly_rate_date: null,
  id: null,
  work_experience_ids: null,
  project: {},
  notice_period: null,
  workload: null,
  cost_center_id: null,
  agreement_link: null,
  automatic_time_reporting: null,
  disable_cv: null,
  reportable: true,
  require_subproject_reports: false,
});
const roleUserName = ref<string>("");
const showWarningVisible = ref<boolean>(false);
const workExperienceOptions = ref<Array<any>>([]);
const showFutureHourlyRateWarningVisible = ref<boolean>(false);
const loadingFutureHourlyRateReports = ref<boolean>(false);
const impactedFutureHourlyRateReports = ref<Array<any>>([]);
const futureHourlyRateReports = ref<Array<any>>([]);

// Watchers
watch(toRef(props, "visible"), (newProp, oldProp) => {
  if (newProp && !oldProp) {
    showWarningVisible.value = false;
    showFutureHourlyRateWarningVisible.value = false;
    futureHourlyRateReports.value = [];
    impactedFutureHourlyRateReports.value = [];
    loadingFutureHourlyRateReports.value = false;

    // Load users
    emits("get-cost-centers");
    emits("get-subprojects-page", props.project.id);

    // Set fields to current values
    newProjectRole.value = setObject(newProjectRole.value, props.role);
    newProjectRole.value.id = props.role.id;
    newProjectRole.value.workload = Math.round(newProjectRole.value.workload * 10000) / 100;
    newProjectRole.value.hourly_rate_cents = props.role.hourly_rate
      ? props.role.hourly_rate.value / 100
      : 0;
    newProjectRole.value.hourly_rate_currency = props.role.hourly_rate
      ? props.role.hourly_rate.currency_code
      : "SEK";
    newProjectRole.value.future_hourly_rate_cents = props.role.future_hourly_rate
      ? props.role.future_hourly_rate.value / 100
      : 0;
    newProjectRole.value.future_hourly_rate_currency = props.role.future_hourly_rate
      ? props.role.future_hourly_rate.currency_code
      : "SEK";
    newProjectRole.value.work_experience_ids =
      props.role.work_experiences?.map((workExperience) => workExperience.id) || [];
    newProjectRole.value.user_id = props.role.user ? props.role.user.id : undefined;
    newProjectRole.value.agreement_link = props.role.agreement_link;
    roleUserName.value = props.role.user.name;
    newProjectRole.value.automatic_time_reporting = props.role.automatic_time_reporting;
    newProjectRole.value.disable_cv = props.role.disable_cv;
    newProjectRole.value.reportable = props.role.reportable !== false;
    newProjectRole.value.require_subproject_reports = props.role.require_subproject_reports;

    if (newProjectRole.value.user_id) {
      apiGetUser(newProjectRole.value.user_id).then((res: any) => {
        const workExperiences = (res.data.data.work_experiences ||
          []) as Array<WorkExperienceInterface>;
        workExperienceOptions.value = workExperiences.map((workExperience) => ({
          id: workExperience.id,
          formatted_name: `${workExperience.title} at ${workExperience.company_name}`,
        }));
      });
    } else {
      workExperienceOptions.value = [];
    }
  }
});

// Component methods
const onSelectChange = (field: string, value: any): void => {
  newProjectRole.value[field] = value;
};

const onDateChange = (date: any, attribute: string): void => {
  if (date === null) {
    newProjectRole.value[attribute] = null;
  } else {
    newProjectRole.value[attribute] = date.format("YYYY-MM-DD");
  }
};

const disableStartDate = (current: any): boolean => {
  if (!current || !newProjectRole.value.end_date) {
    return false;
  }

  return current > moment(newProjectRole.value.end_date).endOf("day");
};

const disableEndDate = (current: any): boolean => {
  if (!current || !newProjectRole.value.start_date) {
    return false;
  }

  return current < moment(newProjectRole.value.start_date).startOf("day");
};

const changeRatesToCents = (): void => {
  newProjectRole.value.hourly_rate_cents = toMoneyCents(newProjectRole.value.hourly_rate_cents);
  newProjectRole.value.future_hourly_rate_cents = toMoneyCents(
    newProjectRole.value.future_hourly_rate_cents
  );
};

const isFutureHourlyRateDateInPast = (): boolean => {
  if (!newProjectRole.value.future_hourly_rate_date) {
    return false;
  }

  return moment(newProjectRole.value.future_hourly_rate_date, "YYYY-MM-DD").isBefore(
    moment().startOf("day"),
    "day"
  );
};

const getFutureHourlyRateReportLabel = (report: ReportInterface): string => {
  const startDate = report.start_date || "Unknown date";
  const endDate =
    report.end_date && report.end_date !== report.start_date ? ` - ${report.end_date}` : "";
  const title = report.title || report.role?.title || "Report";

  return `${startDate}${endDate} - ${title}`;
};

const getConfiguredFutureHourlyRateText = (): string => {
  const rate = newProjectRole.value.future_hourly_rate_cents;
  const currency = newProjectRole.value.future_hourly_rate_currency || "SEK";

  if (rate === null || rate === undefined || Number.isNaN(Number(rate))) {
    return "-";
  }

  const formattedRate = Number(rate).toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${formattedRate} ${currency}`;
};

const getFutureHourlyRateText = (report: ReportInterface): string => {
  return `${toMoney(report.hourly_rate)} -> ${getConfiguredFutureHourlyRateText()}`;
};

const loadFutureHourlyRateReports = async (): Promise<void> => {
  if (!props.role?.id || !newProjectRole.value.future_hourly_rate_date) {
    futureHourlyRateReports.value = [];
    impactedFutureHourlyRateReports.value = [];
    return;
  }

  loadingFutureHourlyRateReports.value = true;
  try {
    const response = await apiGetReports({
      role_id: [props.role.id],
      start_date: newProjectRole.value.future_hourly_rate_date,
      end_date: moment().format("YYYY-MM-DD"),
      page: 1,
      page_size: 1000,
    });

    futureHourlyRateReports.value = response.data.data || [];
    impactedFutureHourlyRateReports.value = response.data.data || [];
  } finally {
    loadingFutureHourlyRateReports.value = false;
  }
};

const handleUpdateProjectRole = (): void => {
  $validator?.errors.clear();
  $validator?.validate().then((result: any) => {
    if (result) {
      if (isFutureHourlyRateDateInPast()) {
        loadFutureHourlyRateReports().then(() => {
          showFutureHourlyRateWarningVisible.value = true;
        });
        return;
      }

      if (hasPendingExtension(props.role)) {
        showWarningVisible.value = true;
      } else {
        finaliseUpdateProjectRole();
      }
    }
  });
};

const continueAfterWarning = (): void => {
  showWarningVisible.value = false;
  finaliseUpdateProjectRole();
};

const cancelAfterWarning = (): void => {
  showWarningVisible.value = false;
  emits("close");
};

const continueAfterFutureHourlyRateWarning = (): void => {
  showFutureHourlyRateWarningVisible.value = false;
  if (hasPendingExtension(props.role)) {
    showWarningVisible.value = true;
  } else {
    finaliseUpdateProjectRole();
  }
};

const cancelAfterFutureHourlyRateWarning = (): void => {
  showFutureHourlyRateWarningVisible.value = false;
};

const finaliseUpdateProjectRole = (): void => {
  changeRatesToCents();
  newProjectRole.value.workload = newProjectRole.value.workload / 100;
  emits("update-project-role", newProjectRole.value);
  emits("close");
};

const closeModal = (): void => {
  emits("close");
};
</script>

<style scoped>
.future-rate-warning {
  margin-top: 8px;
}

.warning-intro {
  margin-bottom: 8px;
  color: #4d4d4d;
}

.warning-loading {
  margin-top: 12px;
  color: #4d4d4d;
}

.report-section-title {
  margin: 12px 0 8px;
  font-weight: 600;
  color: #2b2b2b;
}

.report-list {
  max-height: 240px;
  overflow-y: auto;
  padding-right: 4px;
}

.report-item {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: #fafafa;
}

.report-title {
  font-weight: 600;
  margin-bottom: 6px;
  color: #2b2b2b;
}

.report-rate-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
}

.rate-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8c8c8c;
}

.rate-change {
  font-family:
    "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  color: #1f1f1f;
}

.report-empty {
  margin: 12px 0 0;
  color: #8c8c8c;
}
</style>
